import { Button, Input, Modal, Spinner, useToast } from '@nexo/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Copy, RotateCcw } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { api } from '../../lib/api';
import { errorMessage } from '../../lib/forms';

const FEED_KEY = ['calendar', 'feed'] as const;

/** The private subscription link and how to add it to Google, Outlook or Apple Calendar. */
export function CalendarSyncDialog({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();
  const feed = useQuery({ queryKey: FEED_KEY, queryFn: api.calendar.feed });
  const reset = useMutation({
    mutationFn: api.calendar.reset,
    onSuccess: (fresh) => {
      queryClient.setQueryData(FEED_KEY, fresh);
      toast({ title: t('Enlace nuevo creado; el anterior ya no funciona'), tone: 'success' });
    },
    onError: (err) =>
      toast({ title: t('No se pudo cambiar'), description: errorMessage(err), tone: 'error' }),
  });
  const url = feed.data?.url ?? '';

  const steps = [
    {
      app: 'Google Calendar',
      how: t('Otros calendarios → «+» → «Desde URL» y pega el enlace.'),
    },
    { app: 'Outlook', how: t('Añadir calendario → «Suscribirse desde la web» y pega el enlace.') },
    {
      app: 'Apple Calendar',
      how: t('Archivo → «Nueva suscripción a calendario» y pega el enlace.'),
    },
  ];

  return (
    <Modal
      open
      onClose={onClose}
      title={t('Sincronizar con tu calendario')}
      description={t(
        'Tus reuniones, tus tareas con fecha y los sprints de tus equipos, siempre al día en tu calendario.',
      )}
    >
      {!feed.data ? (
        <div className="flex justify-center py-6 text-muted">
          <Spinner />
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          <div className="flex gap-2">
            <Input
              aria-label={t('Enlace del calendario')}
              readOnly
              value={url}
              onFocus={(e) => e.target.select()}
            />
            <Button
              icon={<Copy className="size-4" />}
              onClick={() => {
                void navigator.clipboard.writeText(url);
                toast({ title: t('Enlace copiado'), tone: 'success' });
              }}
            >
              {t('Copiar')}
            </Button>
          </div>
          <ul className="flex flex-col gap-2 text-sm">
            {steps.map((step) => (
              <li key={step.app}>
                <span className="font-medium">{step.app}:</span>{' '}
                <span className="text-muted">{step.how}</span>
              </li>
            ))}
          </ul>
          <p className="rounded-lg bg-surface-2 p-3 text-xs text-muted">
            {t(
              'El enlace es privado: quien lo tenga puede ver estos eventos. Si lo compartes sin querer, crea uno nuevo. Los calendarios se actualizan cada hora aproximadamente.',
            )}
          </p>
          <Button
            variant="ghost"
            size="sm"
            icon={<RotateCcw className="size-4" />}
            className="self-start"
            loading={reset.isPending}
            onClick={() => reset.mutate()}
          >
            {t('Crear un enlace nuevo')}
          </Button>
        </div>
      )}
    </Modal>
  );
}
